'use client';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useRef, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { GoDotFill } from 'react-icons/go';
import { useCurrentStore } from '../../utils/use-store-utils';
import { useQuery } from '@tanstack/react-query';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { fetchWords } from '../../api/custom/fetches';

export function CategoriesInfiniteCarousel({
  className,
}: {
  className?: { background?: string; text?: string; icon?: string };
}) {
  const locale = useLocaleStore(s => s.currentLocale);
  const { currentStore } = useCurrentStore();

  const { data: wordsArray = { data: [] } } = useQuery({
    queryKey: ['WORDS', currentStore, locale],
    queryFn: () => fetchWords({ locale }),
  });

  const words = wordsArray.data.map(word => word.name) as string[];

  const [carouselWidth, setCarouselWidth] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();

  useEffect(() => {
    const updateWidth = () => {
      if (carouselRef.current) {
        const width = carouselRef.current.scrollWidth / 2;
        setCarouselWidth(width);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    controls.start({
      x: -carouselWidth,
      transition: {
        duration: 110,
        ease: 'linear',
        repeat: Infinity,
        repeatType: 'loop',
      },
    });

    return () => window.removeEventListener('resize', updateWidth);
  }, [carouselWidth, controls]);

  const handleAnimationComplete = () => {
    controls.set({ x: 0 });
    controls.start({
      x: -carouselWidth,
      transition: {
        duration: 110,
        ease: 'linear',
        repeat: Infinity,
        repeatType: 'loop',
      },
    });
  };

  const duplicatedWords = [...words, ...words, ...words, ...words, ...words, ...words, ...words];

  return (
    <div className={cn('w-full bg-primary py-4 sm:py-5', className?.background)}>
      <motion.div
        ref={carouselRef}
        className="flex flex-nowrap"
        animate={controls}
        onAnimationComplete={handleAnimationComplete}
      >
        {duplicatedWords.map((word, index) => (
          <motion.div
            key={`${word}-${index}`}
            className={cn(
              'mx-2 flex items-center justify-center gap-4 whitespace-nowrap text-xl font-semibold text-black sm:mx-2.5 sm:gap-5 sm:text-2xl md:mx-3 md:gap-6 lg:mx-4 lg:gap-8 lg:text-3xl',
              className?.text,
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {word} <GoDotFill size={20} className={cn('text-black', className?.icon)} />
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
